import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import enTranslations from '../translations/en.json';
import plTranslations from '../translations/pl.json';

type TranslationKey = string;
type TranslationParams = Record<string, string | number>;

interface TranslationContextType {
  t: (key: TranslationKey, params?: TranslationParams) => string;
  language: string;
  setLanguage: (lang: string) => void;
  availableLanguages: string[];
}

// Available translations
const translations: Record<string, any> = {
  pl: plTranslations,
  en: enTranslations,
};

// Get nested object value by dot notation key
const getNestedValue = (obj: any, key: string): string => {
  return key.split('.').reduce((current, keyPart) => {
    return current && current[keyPart] !== undefined ? current[keyPart] : null;
  }, obj);
};

// Replace parameters in translation string
const replaceParams = (text: string, params?: TranslationParams): string => {
  if (!params) return text;
  
  return Object.keys(params).reduce((result, key) => {
    const regex = new RegExp(`{${key}}`, 'g');
    return result.replace(regex, String(params[key]));
  }, text);
};

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<string>(() => {
    // Get language from localStorage or default to 'pl'
    return localStorage.getItem('language') || 'pl';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key: TranslationKey, params?: TranslationParams): string => {
    const currentTranslations = translations[language] || translations.en;
    const translation = getNestedValue(currentTranslations, key);
    
    if (translation === null) {
      console.warn(`Translation missing for key: ${key} in language: ${language}`);
      return key; // Return the key itself if translation is missing
    }
    
    return replaceParams(translation, params);
  };

  const setLanguage = (lang: string) => {
    if (translations[lang]) {
      setLanguageState(lang);
    } else {
      console.warn(`Language ${lang} is not available`);
    }
  };

  const value = {
    t,
    language,
    setLanguage,
    availableLanguages: Object.keys(translations),
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export default useTranslation;
